import { useCallback } from 'react';

const ACV_BACKEND_URL = (import.meta.env.VITE_ACV_BACKEND_API as string | undefined)
  || 'https://acvnns-api-produc.acv.app';

export interface StorageInfo {
  usedStorage: number;      // GB
  providedStorage: number;  // GB
}

const getCredentials = (): { slug: string; email: string } => ({
  slug: localStorage.getItem('slug') || '',
  email: localStorage.getItem('userEmail') || '',
});

/** ACV quota APIs expect storage amounts in GB (same as Whiteboard / Assignments). */
const bytesToGB = (bytes: number): number => {
  if (!bytes || !Number.isFinite(bytes)) return 0;
  return bytes / 1024 ** 3;
};

/**
 * Integrates AIRecording with ACV storage quota system.
 * Mirrors ACV/src/utils/storageUsage.js and Whiteboard addUserStorage (GB, not bytes).
 */
export const useStorage = () => {

  const getStorageInfo = useCallback(async (): Promise<StorageInfo | null> => {
    const { slug, email } = getCredentials();
    if (!slug || !email) return null;
    try {
      const res = await fetch(
        `${ACV_BACKEND_URL}/api/storage/user?slug=${encodeURIComponent(slug)}&email=${encodeURIComponent(email)}&role=Teacher`
      );
      if (!res.ok) return null;
      const json = await res.json();
      const payload = json.result ?? json;
      return {
        usedStorage: Number(payload.usedStorage ?? payload.used_storage ?? 0),
        providedStorage: Number(payload.providedStorage ?? payload.provided_storage ?? 10),
      };
    } catch {
      return null;
    }
  }, []);

  /**
   * Returns true if the file fits within the remaining quota.
   * Defaults to true if ACV quota service is unreachable (don't block the user).
   */
  const checkQuota = useCallback(async (fileSizeBytes: number): Promise<boolean> => {
    const info = await getStorageInfo();
    if (!info) return true;
    const fileSizeGB = fileSizeBytes / (1024 ** 3);
    return (info.usedStorage + fileSizeGB) < info.providedStorage;
  }, [getStorageInfo]);

  /**
   * Register storage consumption with ACV after a successful upload.
   * Non-blocking — failure is logged but never throws.
   */
  const addStorage = useCallback(async (fileSizeBytes: number): Promise<void> => {
    const { slug, email } = getCredentials();
    if (!slug || !email) return;
    const storageGB = bytesToGB(fileSizeBytes);
    if (storageGB <= 0) return;
    try {
      await fetch(`${ACV_BACKEND_URL}/api/storage/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug,
          email,
          storage: storageGB,
          role: 'Teacher',
          extension: 'aiRecording',
          feature: 'video',
          type: 'video',
        }),
      });
    } catch (err) {
      console.warn('[Storage] Could not register storage consumption:', err);
    }
  }, []);

  /**
   * Deduct storage from ACV quota after a successful video deletion.
   * Non-blocking — failure is logged but never throws.
   */
  const deductStorage = useCallback(async (fileSizeBytes: number, fileName?: string): Promise<void> => {
    const { slug, email } = getCredentials();
    if (!slug || !email) return;
    const storageGB = bytesToGB(fileSizeBytes);
    if (storageGB <= 0) return;
    try {
      await fetch(`${ACV_BACKEND_URL}/api/storage/deduct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug,
          email,
          storage: storageGB,
          role: 'Teacher',
          extension: 'aiRecording',
          feature: 'video',
          fileName: fileName || '',
          type: 'video',
        }),
      });
    } catch (err) {
      console.warn('[Storage] Could not deduct storage quota:', err);
    }
  }, []);

  return { getStorageInfo, checkQuota, addStorage, deductStorage };
};
